/**
 * #223029 — hình học thuần cho màn khoanh vùng bàn trên ảnh camera (không phụ thuộc React để test bằng
 * node). Toạ độ luôn CHUẨN HOÁ 0..1 theo khung ảnh (trên-trái là gốc), đúng hợp đồng API `region-splits`.
 */


export const MIN_POINTS = 3;
export const MAX_POINTS = 12;
/** Khung tròn (lệnh sếp 27/10/2026) lưu dạng đa giác đều 24 đỉnh — máy chủ nhận tới 32 điểm cho `hinh: "tron"`. */
export const CIRCLE_POINTS = 24;
/** Khung nhỏ hơn mức này (tỉ lệ diện tích ảnh) coi là chạm nhầm, không tạo khung. */
export const MIN_AREA = 0.0008;

export const FRAME_COLORS = [
  "#2f80ed",
  "#eb5757",
  "#27ae60",
  "#f2994a",
  "#9b51e0",
  "#00a3a3",
  "#c2185b",
  "#6d4c41",
];
export const EXCLUDE_COLOR = "#6b7280";

export const clamp01 = (v) => Math.max(0, Math.min(1, v));

/** Điểm chạm (px màn hình) → toạ độ 0..1 trong hộp ảnh. */
export function toNorm(
  clientX,
  clientY,
  box
) {
  if (box.width <= 0 || box.height <= 0) return [0, 0];
  return [
    clamp01((clientX - box.left) / box.width),
    clamp01((clientY - box.top) / box.height),
  ];
}

/** Hai góc kéo → 4 đỉnh chữ nhật (thứ tự theo chiều kim đồng hồ từ trên-trái). */
export function rectPoints(a, b) {
  const x0 = Math.min(a[0], b[0]);
  const x1 = Math.max(a[0], b[0]);
  const y0 = Math.min(a[1], b[1]);
  const y1 = Math.max(a[1], b[1]);
  return [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];
}

/** Diện tích đa giác (công thức dây giày, luôn dương). */
export function polygonArea(p) {
  let s = 0;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    s += p[j][0] * p[i][1] - p[i][0] * p[j][1];
  }
  return Math.abs(s) / 2;
}

const cross = (o, a, b) =>
  (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

/** Hai đoạn p1p2 và p3p4 cắt nhau THẬT (chạm đầu mút chung của cạnh kề không tính). */
export function segmentsCross(p1, p2, p3, p4) {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/** Đa giác tự cắt: cạnh không kề nhau cắt nhau. */
export function isSelfIntersecting(p) {
  const n = p.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = p[i];
    const a2 = p[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      // bỏ cặp cạnh kề nhau (kể cả cạnh cuối nối đỉnh đầu)
      if (j === i || (j + 1) % n === i || (i + 1) % n === j) continue;
      if (segmentsCross(a1, a2, p[j], p[(j + 1) % n])) return true;
    }
  }
  return false;
}

/** Điểm trong đa giác (ray casting). */
export function pointInPolygon(x, y, p) {
  let inside = false;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [xi, yi] = p[i];
    const [xj, yj] = p[j];
    if ((yi > y) !== (yj > y) && x < (((xj - xi) * (y - yi)) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/** Khung chứa điểm (nhiều khung ⇒ nhỏ nhất, để chọn được khung nằm trong khung to); -1 nếu không có. */
export function hitFrame(x, y, frames) {
  let best = -1;
  let bestArea = Infinity;
  frames.forEach((f, i) => {
    if (!pointInPolygon(x, y, f.diem)) return;
    const a = polygonArea(f.diem);
    if (a < bestArea) {
      bestArea = a;
      best = i;
    }
  });
  return best;
}

/** Lỗi dễ hiểu (tiếng Việt) trước khi lưu; null = hợp lệ. */
export function validateFrame(f) {
  if (!f.ten.trim()) return "Khung chưa có tên.";
  if (f.diem.length < MIN_POINTS) return "Khung cần ít nhất 3 điểm.";
  if (f.hinh !== "tron" && f.diem.length > MAX_POINTS) return "Khung tối đa 12 điểm.";
  if (isSelfIntersecting(f.diem)) {
    return `Khung “${f.ten}” bị cắt chéo — vẽ lại cho các cạnh không giao nhau.`;
  }
  if (polygonArea(f.diem) < MIN_AREA) return `Khung “${f.ten}” quá nhỏ.`;
  // Số bàn KHÔNG bắt buộc (lệnh sếp 17/10): khung bàn khách có thể chưa gắn số bàn.
  return null;
}

/** Kiểm cả bộ: lỗi đầu tiên, trùng tên (máy chủ khử trùng theo tên). */
export function validateFrames(frames) {
  const seen = new Set();
  for (const f of frames) {
    const e = validateFrame(f);
    if (e) return e;
    const k = f.ten.trim();
    if (seen.has(k)) return `Hai khung trùng tên “${k}” — đặt tên khác nhau.`;
    seen.add(k);
  }
  return null;
}

export function colorOf(f, index) {
  if (f.loai === "khong_phuc_vu") return EXCLUDE_COLOR;
  return f.mau || FRAME_COLORS[index % FRAME_COLORS.length];
}

/** Khung gửi lên máy chủ (bỏ mã nội bộ, giữ đúng hợp đồng). */
export function toWire(f) {
  return {
    hinh: f.hinh,
    diem: f.diem.map(([x, y]) => [Number(x.toFixed(4)), Number(y.toFixed(4))]),
    ten: f.ten.trim(),
    loai: f.loai,
    ...(f.loai === "ban_that" ? { ban_that: f.ban_that ?? null } : {}),
    ...(f.loai === "ban_con" ? { thuoc_ban_that: f.thuoc_ban_that ?? null } : {}),
    ...(f.mau ? { mau: f.mau } : {}),
  };
}

/** Khung chữ nhật (4 đỉnh) — kéo góc/cạnh thì giữ nguyên chữ nhật. */
export function isRectFrame(f) {
  return f.hinh === "chu_nhat" && f.diem.length === 4;
}

/** Hộp bao [x0, y0, x1, y1] của các điểm. */
export function bounds(p) {
  const xs = p.map((q) => q[0]);
  const ys = p.map((q) => q[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

/** Dời cả khung theo (dx, dy), chặn để khung không ra ngoài ảnh. */
export function translatePoints(p, dx, dy) {
  const [x0, y0, x1, y1] = bounds(p);
  const ddx = Math.max(-x0, Math.min(1 - x1, dx));
  const ddy = Math.max(-y0, Math.min(1 - y1, dy));
  return p.map(([x, y]) => [x + ddx, y + ddy]);
}

/** Kéo góc `k` của chữ nhật tới `to`: góc đối diện đứng yên, vẫn là chữ nhật. */
export function resizeRectCorner(p, k, to) {
  const [x0, y0, x1, y1] = bounds(p);
  const [cx, cy] = p[k];
  const ox = Math.abs(cx - x0) < Math.abs(cx - x1) ? x1 : x0;
  const oy = Math.abs(cy - y0) < Math.abs(cy - y1) ? y1 : y0;
  return rectPoints([ox, oy], [clamp01(to[0]), clamp01(to[1])]);
}

/** Cạnh chữ nhật: 0 trên, 1 phải, 2 dưới, 3 trái — điểm giữa cạnh để vẽ chấm kéo. */
export function rectEdgeHandles(p) {
  const [x0, y0, x1, y1] = bounds(p);
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  return [
    [cx, y0],
    [x1, cy],
    [cx, y1],
    [x0, cy],
  ];
}

/** Kéo riêng một cạnh chữ nhật tới `to` (chỉ đổi toạ độ của cạnh đó). */
export function resizeRectEdge(p, side, to) {
  let [x0, y0, x1, y1] = bounds(p);
  const x = clamp01(to[0]);
  const y = clamp01(to[1]);
  if (side === 0) y0 = y;
  if (side === 1) x1 = x;
  if (side === 2) y1 = y;
  if (side === 3) x0 = x;
  return rectPoints([x0, y0], [x1, y1]);
}

/**
 * Khung tròn tâm `c`, đi qua điểm `edge`. `aspect` = rộng/cao của ảnh: toạ độ 0..1 co giãn khác nhau theo
 * hai chiều nên phải quy về điểm ảnh thì vòng mới TRÒN thật trên ảnh (không thành bầu dục).
 */
export function circlePoints(c, edge, aspect = 1) {
  const a = aspect > 0 ? aspect : 1;
  const r = Math.hypot((edge[0] - c[0]) * a, edge[1] - c[1]);
  return Array.from({ length: CIRCLE_POINTS }, (_, i) => {
    const t = (2 * Math.PI * i) / CIRCLE_POINTS;
    return [clamp01(c[0] + (r * Math.cos(t)) / a), clamp01(c[1] + r * Math.sin(t))];
  });
}

export function isCircleFrame(f) {
  return f.hinh === "tron" && f.diem.length >= MIN_POINTS;
}

/** Tâm của khung tròn = trung bình các đỉnh (đúng cả khi mép bị kẹp sát rìa ảnh). */
export function circleCenter(p) {
  const n = p.length || 1;
  return [p.reduce((s, q) => s + q[0], 0) / n, p.reduce((s, q) => s + q[1], 0) / n];
}

/** 4 chấm kéo bán kính: phải, dưới, trái, trên của vòng tròn. */
export function circleHandles(p) {
  const [x0, y0, x1, y1] = bounds(p);
  const [cx, cy] = circleCenter(p);
  return [
    [x1, cy],
    [cx, y1],
    [x0, cy],
    [cx, y0],
  ];
}
